import React, { useState, useEffect } from 'react';
import emailjs from '@emailjs/browser';
import '../styles/Contact.css';
import Button from './Button';

const Contact = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { threshold: 0.2 }
    );

    const element = document.getElementById('contact');
    if (element) observer.observe(element);

    return () => observer.disconnect();
  }, []);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Send email notification to you (owner)
      await emailjs.send(
        import.meta.env.VITE_EMAILJS_SERVICE_ID || 'service_ipye6ld',
        import.meta.env.VITE_EMAILJS_TEMPLATE_OWNER || 'template_y675ct5',
        {
          from_name: formData.name,
          reply_to: formData.email,
          from_email: formData.email,
          message: formData.message
        },
        import.meta.env.VITE_EMAILJS_PUBLIC_KEY || 'wVn-LZ2QF5zRy4D4f'
      );

      // Send confirmation email to sender
      await emailjs.send(
        import.meta.env.VITE_EMAILJS_SERVICE_ID || 'service_ipye6ld',
        import.meta.env.VITE_EMAILJS_TEMPLATE_USER || 'template_b7ux7n4',
        {
          to_name: formData.name,
          reply_to: import.meta.env.VITE_OWNER_EMAIL || 'prajeethv100@gmail.com',
          user_email: formData.email
        },
        import.meta.env.VITE_EMAILJS_PUBLIC_KEY || 'wVn-LZ2QF5zRy4D4f'
      );
      
      console.log('Emails sent successfully!');
      setSubmitStatus('success');
      setFormData({ name: '', email: '', message: '' });
      
      setTimeout(() => setSubmitStatus(null), 5000);
    } catch (error) {
      console.error('Email sending failed:', error);
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus(null), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const socialLinks = [
    { name: 'GitHub', url: 'https://github.com/Prajeethv21', icon: '⚙️' },
    { name: 'LinkedIn', url: 'https://linkedin.com/in/prajeeth-v-b6a2452b9/', icon: '🔗' },
    { name: 'Email', url: 'mailto:prajeethv100@gmail.com', icon: '✉️' }
  ];

  return (
    <div className={`contact-section ${isVisible ? 'visible' : ''}`}>
      <div className="container">
        <h2 className="section-title">
          <span className="title-number">06.</span>
          Get In Touch
        </h2>

        <div className="contact-content">
          <div className="contact-info">
            <h3 className="contact-heading">Let's Work Together</h3>
            <p className="contact-text">
               I'm open to internship opportunities and exciting collaborative projects where
              I can apply my skills and contribute to meaningful solutions. If you have an idea to build or
              would like to collaborate, I'd love to connect.
            </p>

            <div className="contact-details">
              <div className="contact-item">
                <span className="contact-icon">📍</span>
                <div>
                  <h4>Location</h4>
                  <p>Bangalore, Karnataka</p>
                </div>
              </div>
              <div className="contact-item">
                <span className="contact-icon">✉️</span>
                <div>
                  <h4>Email</h4>
                  <p>prajeethv100@gmail.com</p>
                </div>
              </div>
              <div className="contact-item">
                <span className="contact-icon">☎️</span>
                <div>
                  <h4>Phone</h4>
                  <p>+91 7829229319</p>
                </div>
              </div>
            </div>

            <div className="social-links">
              {socialLinks.map(link => (
                <a
                  key={link.name}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-link"
                  aria-label={link.name}
                >
                  <span className="social-icon">{link.icon}</span>
                  <span className="social-name">{link.name}</span>
                </a>
              ))}
            </div>
          </div>

          <form className="contact-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="name">Your Name</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={errors.name ? 'error' : ''}
                placeholder="Prathik K"
              />
              {errors.name && <span className="error-message">{errors.name}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="email">Your Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={errors.email ? 'error' : ''}
                placeholder="prathik@example.com"
              />
              {errors.email && <span className="error-message">{errors.email}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="message">Message</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                className={errors.message ? 'error' : ''}
                placeholder="Tell me about your project..."
                rows="6"
              />
              {errors.message && <span className="error-message">{errors.message}</span>}
            </div>

            <Button 
              as="button"
              type="submit" 
              className="submit-btn"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner"></span>
                  Sending...
                </>
              ) : (
                'Send Message'
              )}
            </Button>

            {submitStatus === 'success' && (
              <div className="status-message success">
                ✓ Message sent successfully! I'll get back to you soon.
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="status-message error">
                ✗ 
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default Contact;
